/**
 * Central tuning + quality configuration.
 * Subsystems read from here rather than hardcoding magic numbers, so the
 * quality scaler and the capture harness can drive everything from one place.
 */

import { detectTier } from './hwtier.js';
import { IDLE_MODES } from './idle.js';

export { QUALITY_ORDER } from './hwtier.js';

export const PHYSICS_HZ = 120;
export const FIXED_DT = 1 / PHYSICS_HZ;
/** Never simulate more than this many physics steps in one frame (spiral-of-death guard). */
export const MAX_SUBSTEPS = 8;

/** Real-world units are metres, seconds, kilograms. */
export const UNITS = {
  gravity: -9.81 * 2.1, // Games use exaggerated gravity; CoD-like feel.
  playerHeight: 1.78,
  playerCrouchHeight: 1.12,
  playerRadius: 0.32,
  eyeOffset: 0.12, // below top of capsule
};

/**
 * ---------------------------------------------------------------------------
 * QUALITY PRESETS
 * ---------------------------------------------------------------------------
 * `medium`, `high` and `ultra` are frozen: they produce the frames the pixel
 * gate in tools/baseline.mjs was signed off against, and nothing in the
 * performance work is allowed to move them.
 *
 * The two tiers below them answer different questions:
 *
 *   low        the machine cannot run a deferred-ish HDR pipeline at all
 *              (software rasteriser, 2-core netbook). Effects come off because
 *              there is no budget for them at any resolution.
 *
 *   balanced   the machine is an ordinary laptop — Iris Xe, UHD 620, Vega 8,
 *              an MX/T-series mobile chip. This tier exists because `low` was
 *              the only thing under `medium`, and `low` throws away ambient
 *              occlusion, temporal AA, light shafts and contact-hardening
 *              shadows — i.e. most of what makes the game look like the game.
 *              `balanced` keeps ALL of them and pays for it with pixels
 *              instead: half-resolution GTAO, a shorter volumetric march,
 *              1024 cascades, and an internal resolution the governor in
 *              src/render/adaptive.js moves to hold the frame target.
 *
 * Optional per-preset fields (omitted = derive from the tier index, which is
 * what medium/high/ultra do, so their programs are unchanged):
 *   gtaoScale       0..1 internal scale for the GTAO chain (1 = full res)
 *   volumetricSteps march steps for the fog/shafts pass
 *   pcss            force contact-hardening shadows on/off
 *   shadowTaps      'low' | 'medium' | 'high' — PCSS blocker/PCF tap counts
 *   contact, dof    force the screen-space contact shadow / ADS DOF passes
 *   viewSamples     MSAA samples on the viewmodel target
 *   cascadeStagger  refresh distant cascades every N frames (1 = every frame)
 */
export const QUALITY_PRESETS = {
  low: {
    renderScale: 0.72,
    shadowMapSize: 1024,
    cascades: 3,
    shadowDistance: 60,
    taa: false,
    gtao: false,
    ssr: false,
    volumetrics: false,
    motionBlur: false,
    bloom: true,
    anisotropy: 4,
    particleBudget: 2000,
    decalBudget: 64,
    cascadeStagger: 4,
  },
  /**
   * Every effect the game has, at a price an integrated GPU can pay.
   *
   * NOT A REDUCED FEATURE SET. TAA, GTAO, PCSS contact-hardening, screen-space
   * contact shadows, SSR, volumetric shafts, ADS depth of field, motion blur
   * and bloom are all ON — the same list `ultra` runs. What changes is how each
   * one is *sized*, and the sizing was picked from where the frame time
   * actually goes.
   *
   * MEASURED, Iris Xe (Intel 0x9A49) at 1920x1080, `hero` shot, ultra:
   *   whole frame                                     35.6 ms  (28 fps)
   *   of which the four shadow cascades               14.9 ms  (42%)
   *
   * The cascades dominate because the caster counts come back as
   * [179, 208, 210, 210] out of a 210-object draw list: the level is a few
   * large merged and instanced meshes, so per-cascade frustum culling cannot
   * reject anything and every cascade redraws the whole world. That is where
   * this tier takes its budget from — three cascades instead of four, 1024
   * instead of 2048, and a stagger that refreshes the distant two at a third of
   * the frame rate. None of that removes an effect; it changes how often a
   * shadow map that was already nearly static gets rebuilt.
   *
   * The rest is resolution: GTAO marches at half res behind a joint bilateral
   * upsample, the volumetric march is 24 steps instead of 56, and the frame
   * starts at 85% internal scale — from where the governor takes over and moves
   * it, up as readily as down.
   */
  balanced: {
    renderScale: 0.85,
    shadowMapSize: 1024,
    cascades: 3,
    shadowDistance: 75,
    taa: true,
    gtao: true,
    gtaoScale: 0.5,
    ssr: true,
    volumetrics: true,
    volumetricSteps: 24,
    motionBlur: true,
    bloom: true,
    contact: true,
    dof: true,
    // Contact-hardening kept on below tier 2 for the first time — a shadow that
    // sharpens where the caster touches the floor is most of what sells a
    // shadow at all. Paid for with the minimum tap set (8 blocker / 10 PCF).
    pcss: true,
    shadowTaps: 'low',
    viewSamples: 2,
    cascadeStagger: 3,
    anisotropy: 8,
    particleBudget: 5000,
    decalBudget: 96,
  },
  medium: {
    renderScale: 0.85,
    shadowMapSize: 2048,
    cascades: 3,
    shadowDistance: 90,
    taa: true,
    gtao: true,
    ssr: false,
    volumetrics: true,
    motionBlur: true,
    bloom: true,
    anisotropy: 8,
    particleBudget: 6000,
    decalBudget: 128,
  },
  high: {
    renderScale: 1.0,
    shadowMapSize: 2048,
    cascades: 4,
    shadowDistance: 140,
    taa: true,
    gtao: true,
    ssr: true,
    volumetrics: true,
    motionBlur: true,
    bloom: true,
    anisotropy: 16,
    particleBudget: 12000,
    decalBudget: 256,
  },
  ultra: {
    renderScale: 1.0,
    shadowMapSize: 4096,
    cascades: 4,
    shadowDistance: 200,
    taa: true,
    gtao: true,
    ssr: true,
    volumetrics: true,
    motionBlur: true,
    bloom: true,
    anisotropy: 16,
    particleBudget: 24000,
    decalBudget: 512,
  },
};

export const DEFAULTS = {
  /**
   * `auto` asks src/core/hwtier.js which tier this machine should start on.
   *
   * It used to be `ultra`, unconditionally, for everybody. That is why the
   * game was unplayable on hardware without a discrete GPU: nothing ever
   * measured the machine, so a laptop got 4x2048 cascades, full-resolution
   * GTAO, SSR and a 56-step volumetric march at native resolution.
   */
  quality: 'auto',
  fov: 80, // horizontal-ish vertical FOV, CoD default feel
  adsFovScale: 0.72,
  sensitivity: 0.0022,
  adsSensScale: 0.65,
  invertY: false,
  exposure: 1.0,
  /** Capture mode disables anything nondeterministic so screenshots are stable. */
  deterministic: false,

  // ---- adaptive resolution (src/render/adaptive.js) ------------------------
  /**
   * Let the renderer move its INTERNAL resolution to hold `targetFps`. The
   * display resolution, the effect list and every material are untouched — the
   * only thing that changes is how many pixels the fill-bound passes shade.
   *
   * It moves in both directions: a preset's `renderScale` is where the frame
   * STARTS, and a machine with headroom climbs from there toward native. A
   * machine already at native and making the target never moves at all, so
   * this costs nothing on hardware that does not need it.
   */
  adaptiveResolution: true,
  targetFps: 48,
  /** Never go below this fraction of the display resolution. */
  minRenderScale: 0.58,

  // ---- idle governor (src/core/idle.js) ------------------------------------
  /**
   * What to do about frames nobody is looking at. NOT a quality setting: it
   * changes no preset, removes no effect and moves no pixel in any state the
   * player can see. It stops the pipeline re-rendering a *frozen* paused frame
   * at the refresh rate, and stops it drawing into a hidden tab at all.
   *
   *   'off'         draw every frame, unconditionally (pre-governor behaviour)
   *   'safe'        skip only hidden-document and settled-pause frames
   *   'background'  also drop to ~10 fps while the window is unfocused; the
   *                 simulation still runs at full rate
   *
   * The simulation is never gated in any mode, so gameplay, networking and
   * physics are bit-identical to 'off' whichever one is active.
   */
  idleMode: 'safe',
};

export function createConfig(overrides = {}) {
  const cfg = { ...DEFAULTS, ...overrides };

  // Resolve `auto` before anything reads cfg.q. Detection is a synchronous
  // string probe against a 1x1 throwaway context — a fraction of a millisecond.
  if (cfg.quality === 'auto') {
    cfg.autoQuality = true;
    try {
      const t = detectTier();
      cfg.quality = t.quality;
      cfg.gpu = t.renderer;
      console.info(`[config] auto quality → ${t.quality} · ${t.renderer} · ${t.reason}`);
    } catch (err) {
      cfg.quality = 'high';
      console.warn('[config] GPU detection failed, defaulting to high', err);
    }
  }

  if (!QUALITY_PRESETS[cfg.quality]) {
    console.warn(`[config] unknown quality "${cfg.quality}", using high`);
    cfg.quality = 'high';
  }

  // Spreading `overrides` copies an explicit `undefined` over the default, and
  // `?idle=` can carry anything, so validate rather than trust.
  if (!IDLE_MODES.includes(cfg.idleMode)) cfg.idleMode = DEFAULTS.idleMode;

  cfg.q = { ...QUALITY_PRESETS[cfg.quality] };
  cfg.setQuality = (name) => {
    if (!QUALITY_PRESETS[name]) throw new Error(`unknown quality preset "${name}"`);
    cfg.quality = name;
    cfg.autoQuality = false;
    // Replace rather than merge: presets declare different OPTIONAL fields
    // (gtaoScale, pcss, shadowTaps...), and Object.assign would leave the
    // previous preset's ones behind on the new one.
    for (const k of Object.keys(cfg.q)) delete cfg.q[k];
    Object.assign(cfg.q, QUALITY_PRESETS[name]);
  };
  return cfg;
}
